import { Empty, Flex, Grid, Skeleton, Table, Tag, Tooltip, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { LoadError } from '@/shared/ui/LoadError'
import { isKnownAction, isKnownResult, type MyLogin } from './loginsApi'
import type { MyLoginsState } from './useMyLogins'

/**
 * Saqlanmagan maydon — KULRANG CHIZIQCHA VA IZOH, bo'sh katak emas.
 *
 * ⚠️ Egasi qarori (2026-08-20): «Ularni "noma'lum" deb ko'rsat, "yo'q"
 * deb EMAS». Farq ma'noda: bo'sh katak «bu urinishda IP bo'lmagan»
 * degan yolg'onni beradi, aslida esa qiymat o'sha paytda YOZILMAGAN va
 * u endi hech qachon to'lmaydi — `platform.audit_log` o'zgarmas.
 */
function Unknown() {
  const { t } = useTranslation()
  return (
    <Tooltip title={t('myLogins.unknownHint')}>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {t('common.dash')} {t('myLogins.unknown')}
      </Typography.Text>
    </Tooltip>
  )
}

/**
 * ⚠️ RANG FAQAT TANISH KODGA. Notanish natijani yashil yoki qizil qilib
 * ko'rsatish xavfsizlik ekranida noto'g'ri xulosa berardi — u rangsiz
 * teg bo'lib, kodning O'ZI bilan chiqadi.
 */
function ResultTag({ result }: { result: string }) {
  const { t } = useTranslation()
  if (!isKnownResult(result)) return <Tag>{result}</Tag>
  return (
    <Tag color={result === 'OK' ? 'green' : 'red'}>{t(`myLogins.result.${result}`)}</Tag>
  )
}

function ActionText({ action }: { action: string }) {
  const { t } = useTranslation()
  return <>{isKnownAction(action) ? t(`myLogins.action.${action}`) : action}</>
}

/**
 * ⚠️ USTUN SARLAVHASI «Qurilma» EMAS, «Qurilma (brauzer aytgan)».
 *
 * `User-Agent` — MIJOZ yuboradigan matn va uni o'zgartirish oson
 * (`curl -A "..."`). U dalil emas, KO'RSATMA, va xavfsizlik qarori unga
 * tayanmasligi kerak. Sarlavhaning o'zi buni aytadi, tooltip esa
 * sababini yozadi: aks holda odam «demak mening telefonimdan kirilgan»
 * degan tekshirilmagan xulosaga kelardi.
 */
function DeviceHeader() {
  const { t } = useTranslation()
  return (
    <Tooltip title={t('myLogins.deviceHint')}>
      <span>{t('myLogins.col.device')}</span>
    </Tooltip>
  )
}

function DeviceText({ userAgent }: { userAgent: string | null }) {
  if (userAgent === null) return <Unknown />
  return (
    <Tooltip title={userAgent}>
      <span>{userAgent}</span>
    </Tooltip>
  )
}

/**
 * Kirish urinishlari ro'yxati — egasi talabi (2026-08-20): «qachon
 * kirdi, qaysi qurilma qaysi IP dan. Status ham — rad etildi yoki
 * muvaffaqiyatli, barchasini ko'rsatish kerak».
 *
 * ⚠️ RAD ETILGAN URINISHLAR RO'YXATDAN OLIB TASHLANMAYDI — ular bu
 * ekranning asosiy ma'nosi: begona odam parolni bilib, TOTP da
 * to'xtagan bo'lsa buni FAQAT shu yerdan bilish mumkin.
 *
 * ⚠️ Telefonda jadval emas, kartochka — `OperationsList` bilan ayni
 * qaror: olti ustun 390px da bir-biriga yopishadi.
 */
export function LoginsList({ state }: { state: MyLoginsState }) {
  const { t } = useTranslation()
  const screens = Grid.useBreakpoint()
  const isMobile = screens.md === false

  if (state.loading) return <Skeleton active paragraph={{ rows: 4 }} />
  if (state.error) return <LoadError titleKey="myLogins.loadFailed" error={state.error} />

  const rows = state.page?.rows ?? []
  if (rows.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('myLogins.empty')} />
  }

  if (isMobile) {
    return (
      <Flex vertical gap={10}>
        {rows.map((row, index) => (
          <Flex
            key={`${row.at}-${index}`}
            vertical
            gap={2}
            style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', paddingBottom: 8 }}
          >
            <Flex justify="space-between" align="baseline" gap={8}>
              <Typography.Text strong style={{ fontSize: 13 }}>
                {formatDateTime(row.at)}
              </Typography.Text>
              <ResultTag result={row.result} />
            </Flex>
            <Typography.Text style={{ fontSize: 13 }}>
              <ActionText action={row.action} />
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('myLogins.col.ip')}: {row.ip ?? <Unknown />}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }} ellipsis>
              {t('myLogins.col.device')}: {row.userAgent ?? <Unknown />}
            </Typography.Text>
            {row.message === null ? null : (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {row.message}
              </Typography.Text>
            )}
          </Flex>
        ))}
      </Flex>
    )
  }

  return (
    <Table<MyLogin>
      dataSource={rows}
      rowKey={(row) => `${row.at}-${row.action}-${row.result}-${row.ip ?? ''}`}
      // ⚠️ `pagination` YO'Q va `showTotal` ham: server jami sonni
      // BERMAYDI (`hasNext` bor, `total` yo'q). antd sahifalagichi
      // qatorlar sonidan «Jami 50» deb yozib qo'yardi va bu YASALGAN
      // son bo'lardi. Sahifadan sahifaga o'tish sahifaning O'ZIDA.
      pagination={false}
      size="small"
      columns={[
        {
          title: t('myLogins.col.at'),
          width: 180,
          render: (_v, row) => (
            <Tooltip title={utcTooltip(row.at)}>
              <span>{formatDateTime(row.at)}</span>
            </Tooltip>
          ),
        },
        {
          title: t('myLogins.col.result'),
          width: 120,
          render: (_v, row) => <ResultTag result={row.result} />,
        },
        {
          title: t('myLogins.col.action'),
          width: 220,
          render: (_v, row) => <ActionText action={row.action} />,
        },
        {
          title: t('myLogins.col.ip'),
          width: 150,
          render: (_v, row) => (row.ip === null ? <Unknown /> : row.ip),
        },
        {
          title: <DeviceHeader />,
          ellipsis: true,
          render: (_v, row) => <DeviceText userAgent={row.userAgent} />,
        },
        {
          title: t('myLogins.col.message'),
          ellipsis: true,
          width: 240,
          render: (_v, row) => (row.message === null ? t('common.dash') : row.message),
        },
      ]}
    />
  )
}
